import * as React from 'react';
import { Skeleton, Typography } from '@mui/material';
import Grid2 from '@mui/material/Grid2';

import {
  GlassCard,
  GlassCategoryCard,
  GlassCategoryIcon,
  GlassShadows,
} from '../../../components/glassmorphism/GlassmorphismStyles';

interface MainCategoriesGridSkeletonProps {
  useGlassCard?: boolean;
  withStepper?: boolean;
}

const CategoryCardSkeleton = ({
  useGlassCard = false,
}: {
  useGlassCard?: boolean;
}) => {
  const CardComponent = useGlassCard ? GlassCard : GlassCategoryCard;

  return (
    <CardComponent
      sx={{
        padding: { xs: '18px 12px', sm: '32px' },
        maxWidth: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '32px',
        aspectRatio: { xs: 1, sm: 'unset' },
        flexDirection: { xs: 'column', sm: 'row' },
        flexShrink: 0,
        textAlign: 'center',
        pointerEvents: 'none',
        '&:hover': {
          boxShadow: GlassShadows.cardShadowHover,
        },
      }}
    >
      <GlassCategoryIcon
        sx={{
          width: { xs: '48px', sm: '80px', tablet: '96px' },
          height: { xs: '48px', sm: '80px', tablet: '96px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: { xs: '12px', sm: 0 },
          mr: { xs: 'auto', sm: '24px', tablet: '32px' },
        }}
      >
        <Skeleton
          variant="circular"
          animation="wave"
          sx={{
            width: { xs: '48px', sm: '80px', tablet: '96px' },
            height: { xs: '48px', sm: '80px', tablet: '96px' },
            transform: 'none',
          }}
        />
      </GlassCategoryIcon>

      <Typography
        variant="h4"
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Skeleton
          variant="text"
          animation="wave"
          sx={{
            width: { xs: '90%', sm: '75%' },
            maxWidth: { xs: 120, sm: 220, tablet: 260 },
            height: { xs: 28, sm: 36, tablet: 42 },
            transform: 'none',
          }}
        />
      </Typography>
    </CardComponent>
  );
};

const RailStepperSkeleton = () => {
  return (
    <Grid2 size={{ xs: 12 }}>
      <Grid2
        container
        sx={{
          mt: { xs: 1, sm: 2 },
          alignItems: 'center',
        }}
      >
        <Grid2 size={{ xs: 12 }}>
          <Skeleton
            variant="rounded"
            animation="wave"
            sx={{
              width: '100%',
              height: 8,
              borderRadius: '999px',
              transform: 'none',
            }}
          />
        </Grid2>

        <Grid2
          size={{ xs: 12 }}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1.5,
          }}
        >
          {[1, 2, 3].map((item) => (
            <Skeleton
              key={item}
              variant="text"
              animation="wave"
              sx={{
                width: 64,
                height: 24,
                transform: 'none',
              }}
            />
          ))}
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

const MainCategoriesGridSkeleton = ({
  useGlassCard = false,
  withStepper = true,
}: MainCategoriesGridSkeletonProps) => {
  return (
    <Grid2
      container
      spacing={{ xs: 2, sm: 3 }}
      sx={{ maxWidth: '760px', mx: 'auto' }}
    >
      {[1, 2, 3, 4].map((item) => (
        <Grid2 size={{ xs: 6 }} key={item}>
          <CategoryCardSkeleton useGlassCard={useGlassCard} />
        </Grid2>
      ))}

      {!useGlassCard && withStepper && <RailStepperSkeleton />}
    </Grid2>
  );
};

export default MainCategoriesGridSkeleton;